import { Checkbox } from "@headlessui/react";

import { JSX, useReducer } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { UpdateProfileForm } from "@/pages/gate/UpdateProfilePage.tsx";

import Button from "@/components/buttons/Button.tsx";
import { UpdateProfileProps, UpdateProfileTab } from "@/components/gate/update-profile/types.ts";
import { IconCheck } from "@/components/icons";
import UpdateProfileLayout from "@/components/layout/UpdateProfileLayout.tsx";

import { useContentLanguages } from "@/hooks/ui/useContentLanguages.ts";
import { cn } from "@/utils/cn.ts";

interface State extends Pick<UpdateProfileForm, "content_all" | "content_ru" | "content_uz"> {
  isError: boolean;
}

enum ActionType {
  TOGGLE_ALL = "TOGGLE_ALL",
  TOGGLE_RU = "TOGGLE_RU",
  TOGGLE_UZ = "TOGGLE_UZ",
  TOGGLE_ERROR = "TOGGLE_ERROR",
}

type Action =
  | { type: ActionType.TOGGLE_ALL }
  | { type: ActionType.TOGGLE_RU; value: boolean }
  | { type: ActionType.TOGGLE_UZ; value: boolean }
  | { type: ActionType.TOGGLE_ERROR };

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case ActionType.TOGGLE_ALL:
      return {
        content_all: !state.content_all,
        content_ru: !state.content_all,
        content_uz: !state.content_all,
        isError: false,
      };
    case ActionType.TOGGLE_RU:
      return { ...state, content_ru: action.value, content_all: action.value && state.content_uz, isError: false };
    case ActionType.TOGGLE_UZ:
      return { ...state, content_uz: action.value, content_all: state.content_ru && action.value, isError: false };
    case ActionType.TOGGLE_ERROR:
      return { ...state, isError: true };
    default:
      return state;
  }
};

const ContentLangTab = ({ setTab }: UpdateProfileProps): JSX.Element => {
  const { t } = useTranslation();
  const { handleSubmit, setValue, getValues } = useFormContext<UpdateProfileForm>();
  const languages = useContentLanguages();

  const initialState: State = {
    content_all: getValues("content_all"),
    content_ru: getValues("content_ru"),
    content_uz: getValues("content_uz"),
    isError: false,
  };

  const [state, dispatch] = useReducer(reducer, initialState);

  const onSubmit = handleSubmit(() => {
    if (!state.content_all && !state.content_ru && !state.content_uz) {
      dispatch({ type: ActionType.TOGGLE_ERROR });
      return;
    }
    setValue("content_all", state.content_all);
    setValue("content_ru", state.content_ru);
    setValue("content_uz", state.content_uz);
    setTab(UpdateProfileTab.FORM);
  });

  const onBack = () => {
    setTab(UpdateProfileTab.FORM);
  };

  const onClick = (lang: keyof State) => {
    switch (lang) {
      case "content_all":
        dispatch({ type: ActionType.TOGGLE_ALL });
        break;
      case "content_ru":
        dispatch({ type: ActionType.TOGGLE_RU, value: !state.content_ru });
        break;
      case "content_uz":
        dispatch({ type: ActionType.TOGGLE_UZ, value: !state.content_uz });
        break;
      default:
        break;
    }
  };

  return (
    <UpdateProfileLayout title={t("Profile.content-language")} onBackClick={onBack}>
      <p className={"mt-4 text-lg/6 max-md:self-start max-md:text-sm/4"}>{t("Profile.content-language-message")}</p>
      <form onSubmit={onSubmit} className={"mt-6 w-full max-w-[27.125rem] max-md:mt-2 max-md:max-w-none"}>
        <div className={"flex flex-col gap-3"}>
          {languages.map((lang) => (
            <Checkbox
              as={"button"}
              type={"button"}
              onChange={() => onClick(lang.value)}
              className={cn(
                "bg-surface-container flex items-center gap-3 rounded-2xl p-4 outline outline-1 -outline-offset-1 outline-transparent transition-outline max-md:py-3",
                {
                  "outline-red-1": state.isError,
                },
              )}
            >
              <span
                className={cn("rounded p-1 outline outline-1 -outline-offset-1 outline-grey-1 transition-all", {
                  "bg-orange-1 outline-transparent": state[lang.value],
                })}
              >
                <IconCheck
                  className={cn(
                    "size-4 stroke-transparent transition-colors",
                    state[lang.value] && "stroke-on-primary",
                  )}
                />
              </span>
              {lang.text}
            </Checkbox>
          ))}
        </div>
        <div className={"mt-6 flex justify-end max-md:mt-3"}>
          <Button
            type={"submit"}
            aria-label={t("Actions.continue")}
            className={"w-[14.375rem] max-md:py-4 max-sm:w-full"}
          >
            {t("Actions.continue")}
          </Button>
        </div>
      </form>
    </UpdateProfileLayout>
  );
};

export default ContentLangTab;
